/* eslint-disable react/no-unused-prop-types */
import React, {Component} from 'react';
import Griddle from 'griddle-react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import {debounce} from 'lodash';
import axios from 'axios';
import classNames from 'classnames';

/*
 */
import LoadingSpinner from 'js/components/loading_spinner';
import 'scss/griddle_datatable.scss';
import {clamp} from 'js/common/number_helper';
import {queryEditRequest} from '../graphql/queries/queryEditRequest';

/** @typedef {import('axios').CancelToken} CancelToken */
/** @typedef {import('axios').CancelTokenSource | null} CancelTokenSource */

/** @typedef {import('griddle-react').ColumnMetaData<any>} GriddleColumnMetadata */
/** @typedef {import('griddle-react').GriddleProps<any>} GriddleProps */
/** @typedef {import('griddle-react').RowMetaData<any>} GriddleRowMetadata */

/** @typedef {typeof SORT_ASCENDING | typeof SORT_DESCENDING} OrderDir */

/**
 * @typedef {Object} DatatableColumn
 * @property {string} [title]
 * @property {string} [field_alias]
 * @property {string} [field_name]
 * @property {string} [data]
 * @property {string} [name]
 * @property {boolean} [searchable]
 * @property {{value: string; regex: boolean}} [search]
 * @property {boolean} [orderable]
 * @property {DatatableColumnMetadataMeta} [meta]
 */

/**
 * @typedef {Object} DatatableColumnMetadataMeta
 * @augments {GriddleColumnMetadata}
 * @property {(props: any) => JSX.Element | null} [customComponent]
 * @property {boolean} [searchable]
 * @property {boolean} [orderable]
 * @property {string} [orderOp]
 */

/**
 * @typedef {Object} DatatableProps
 * @property {boolean} [settings]
 * @property {(message:string|{type:string;message:string})=>void} [showNotification]
 * @property {boolean} [show]
 * @property {string} [url]
 * @property {string[]} [classes]
 * @property {DatatableColumn[]} [columns]
 * @property {string[]} [metadataColumns]
 * @property {Record<string, any> & Partial<DatatableState>} [customConfiguration]
 * @property {boolean} [hidden]
 * @property {Partial<DatatableState>} [initialState]
 * @property {React.MutableRefObject<any>} [innerRef]
 * @property {boolean} [filter]
 * @property {Record<string, any>} [filters]
 * @property {React.RefObject<any>} [forwardRef]
 * @property {boolean} [loadOnMount]
 * @property {Function} [mutateFilter]
 * @property {boolean} [setting]
 * @property {string} [settingsClass]
 * @property {boolean} [useExternalColumns]
 * @property {() => CancelToken} [createCancelToken]
 * @property {() => void} [cancelRequest]
 * @property {(result?: {rawData?: *, data?: *, total?: number}) => void} [onNewDataLoaded]
 * @property {GriddleRowMetadata} [rowMetadata]
 * @property {(props: any) => JSX.Element | null} [customRowComponent]
 * @property {(props: any) => JSX.Element | null} [customPagerComponent]
 */

/**
 * @typedef {Object} DatatableState
 * @property {boolean} isLoading
 * @property {number} savedPage
 * @property {Array} results
 * @property {Array} globalData
 * @property {GriddleColumnMetadata[]} columnMetadata
 * @property {number} currentPage
 * @property {number} currentPageSize
 * @property {number} maxPages
 * @property {string} currentFilter
 * @property {number} currentOrderColumn
 * @property {OrderDir} currentOrderDir
 * @property {number} draw - This is some bullshit cache busting thing that the
 * yarja datatables laravel package uses.
 * @property {any} [currentQuery]
 * @property {Error} [error]
 * @property {string} [externalSortColumn]
 * @property {boolean} [externalSortAscending]
 * @property {string[]} [externalColumns]
 * @property {number} [totalQueryResults]
 */

/**
 * @param {React.ComponentClass<DatatableProps>} ChildComponent
 */
function withCancelToken(ChildComponent) {
    /** @type {React.MutableRefObject<CancelTokenSource>} */
    const tokenSourceRef = React.createRef();
    function createCancelToken() {
        tokenSourceRef.current = axios.CancelToken.source();
        return tokenSourceRef.current.token;
    }
    function cancelRequest() {
        if (tokenSourceRef.current != null) {
            tokenSourceRef.current.cancel();
            tokenSourceRef.current = null;
        }
    }

    /**
     * @class WithCancelToken
     * @extends {React.Component<DatatableProps, DatatableState>}
     */
    class WithCancelToken extends React.Component {
        render() {
            return (
                <ChildComponent
                    {...this.props}
                    innerRef={this.props.forwardRef}
                    createCancelToken={createCancelToken}
                    cancelRequest={cancelRequest}
                />
            );
        }
    }
    return WithCancelToken;
}
/**
 * Remove all properties from external data entries that are not in the column definition list.
 * @param {string[]} columnKeys
 * @param {Record<string, any & {index:number}>[]} results
 */
const extractColumns = (columnKeys, results = []) =>
    results.map((row, index) => ({
        ...R.pick(columnKeys, row),
        index
    }));

export function DatatableLoading() {
    return <div style={{textAlign: 'center'}}>Loading</div>;
}

/**
 * @class DatatablePager
 * @augments{React.Component<import('griddle-react').CustomPagerComponentProps>}
 */
export class DatatablePager extends Component {
    render() {
        const {currentPage, maxPage, nextText, previousText, setPage, next, previous} = this.props;

        /** @type {React.MouseEventHandler<HTMLButtonElement>} */
        const onPageNumberClick = e => {
            e.preventDefault();
            setPage(parseInt(e.currentTarget.getAttribute('data-page-number'), 10));
        };

        const pageLinks = (
            <React.Fragment>
            <li key="previous" className="page-number">
                <button onClick={previous} disabled={currentPage === 0} className={currentPage === 0 && 'disabled'}>
                    {previousText}
                </button>
            </li>
            {Array.from({length: maxPage}).map((_, i) => {
                let className = 'page-number';
                if (i === currentPage) {
                    className = 'page-number active';
                }
                return (
                <li key={i} className={className}>
                    <button data-page-number={i} onClick={onPageNumberClick}>
                        {i + 1}
                    </button>
                </li>
                );
            })}
            <li key="next" className="page-number">
                <button onClick={next} disabled={currentPage === maxPage - 1} className={currentPage === maxPage - 1 && 'disabled' }>
                    {nextText}
                </button>
            </li>
            </React.Fragment>
        );

        return (
            <div className="row">
                <div className="pull-right col-md-12" style={{backgroundColor: 'transparent'}}>
                    <ul className="pull-right">{pageLinks}</ul>
                </div>
            </div>
        );
    }
}

DatatablePager.defaultProps = {
    maxPage: 0,
    nextText: '',
    previousText: '',
    currentPage: 0
};

/** @type {'desc'} */
export const SORT_ASCENDING = 'desc';
/** @type {'asc'} */
export const SORT_DESCENDING = 'asc';

const rootPropTypes = {
    url: PropTypes.string,
    initialState: PropTypes.shape({
        isLoading: PropTypes.bool,
        results: PropTypes.array,
        currentPage: PropTypes.number,
        currentPageSize: PropTypes.number,
        maxPages: PropTypes.number,
        currentFilter: PropTypes.string,
        currentOrderColumn: PropTypes.number,
        currentOrderDir: PropTypes.oneOf([SORT_ASCENDING, SORT_DESCENDING])
    }),
    columns: PropTypes.array,
    // @todo There may be issues with the mutateFilter because of request dropping
    mutateFilter: PropTypes.func,
    showNotification: PropTypes.func,
    filter: PropTypes.bool,
    // @todo: dedupe `show` and `settings` to `showSettings`
    show: PropTypes.bool,
    settings: PropTypes.bool,
    settingsClass: PropTypes.string,
    rowMetadata: PropTypes.object,
    onNewDataLoaded: PropTypes.func,
    loadOnMount: PropTypes.bool,
    orderColumnName: PropTypes.string
};

/**
 * @class DatatablesGridComponent
 * @extends {Component<DatatableProps, DatatableState>}
 */
class DatatablesGridComponent extends Component {
    static propTypes = rootPropTypes;
    /** @type {DatatableProps} */
    static defaultProps = {
        url: '',
        columns: [],
        metadataColumns: ['index'],
        loadOnMount: true,
        mutateFilter: d => d,
        showNotification: notification => {
            let message = 'Invalid response received. Please try again!';
            if (typeof notification === 'object' && !!notification.message) {
                message = notification.message;
            } else if (typeof notification === 'string' && !!notification) {
                message = notification;
            }
            // eslint-disable-next-line no-alert
            return alert(message);
        },
        settingsClass: 'settings btn btn-default',
        useExternalColumns: true
    };
    /** @type {DatatableState} */
    static defaultInitialState = {
        isLoading: false,
        savedPage: 0,
        results: [],
        columnMetadata: [],
        currentPage: 0,
        currentPageSize: 5,
        maxPages: 1,
        currentFilter: '',
        currentOrderColumn: 0,
        currentOrderDir: SORT_ASCENDING,
        globalData: [],
        draw: 0
    };
    latestRequestId = 0;
    griddle = React.createRef();

    /**
     * @param {DatatableProps} props
     */
    constructor(props) {
        super(props);
        /** @type {DatatableState} */
        this.state = {
            ...DatatablesGridComponent.defaultInitialState,
            ...this.props.initialState,
            columnMetadata: this.buildColumnMetadata(props.columns)
        };
    }

    /**
     * @param {DatatableProps['columns']} rawColumnMetadata
     * @param {Record<string, any>[]} rawResults
     * @returns {GriddleColumnMetadata[]}
     */
    buildColumnMetadata = (rawColumnMetadata, rawResults = []) => {
        /** @type {GriddleColumnMetadata[]} */
        const columnMetadata = rawColumnMetadata.map(rawColumnMetadatum => ({
            columnName: rawColumnMetadatum.data,
            displayName: rawColumnMetadatum.title,
            rawResults,
            ...rawColumnMetadatum,
            ...(rawColumnMetadatum.meta !== undefined ? rawColumnMetadatum.meta : {})
        }));
        return columnMetadata;
    };

    componentDidMount() {
        if (this.props.loadOnMount) {
            this.getExternalData();
        }
    }

    componentWillUnmount() {
        this.props.cancelRequest();
    }

    componentDidUpdate(prevProps) {
        const {url: prevUrl} = prevProps;
        const {url: currUrl} = this.props;

        if (currUrl !== prevUrl) {
            this.getExternalData();
        }
    }

    /**
     * @typedef {Object} ColumnOrder
     * @property {DatatableState['currentOrderColumn']} column
     * @property {DatatableState['currentOrderDir']} dir
     */

    /**
     * @typedef {Object} AjaxParameters
     * @property {number} [draw]
     * @property {DatatableColumn[]} [columns]
     * @property {ColumnOrder[]} [order]
     * @property {number} [start]
     * @property {number} [length]
     * @property {DatatableColumn['search']} [search]
     */

    /**
     * Build Query String Parameters object for Datatables call.
     * @returns {AjaxParameters}
     */
    buildAjaxParameters = ({
        page = this.state.currentPage,
        filter = this.state.currentFilter,
        orderColumn = this.state.currentOrderColumn,
        orderDir = this.state.currentOrderDir,
        pageSize = this.state.currentPageSize
    } = {}) => {
        /** @type {AjaxParameters} */
        const params = {
            draw: this.state.draw,
            columns: [],
            order: [
                {
                    column: orderColumn,
                    dir: orderDir
                }
            ],
            start: page * pageSize,
            length: pageSize,
            search: {
                value: filter,
                regex: false
            }
        };

        const columnCount = this.props.columns.length;
        for (let i = 0; i < columnCount; i++) {
            const column = this.props.columns[i];
            params.columns.push({
                data: column.data,
                name: column.name,
                searchable: column.searchable,
                orderable: column.orderable,
                search: {
                    value: '',
                    regex: false
                }
            });
        }

        return params;
    };

    /**
     * @returns {Promise<void>}
     */
    getExternalData = () => {
        const {columns, cancelRequest, createCancelToken} = this.props;
        const {
            currentOrderColumn,
            currentOrderDir,
            currentPageSize,
            currentPage,
            currentFilter
        } = this.state;
        const externalSortColumn =
            currentOrderColumn != null && columns[currentOrderColumn]
                ? columns[currentOrderColumn].data
                : '';
        const externalSortAscending =
            currentOrderDir != null ? currentOrderDir === SORT_DESCENDING : true;
        const externalColumns = this.props.columns.map(column => column.data);

        // Cancel any in-flight requests to prevent out of order response state.
        cancelRequest();

        this.setState(
            {
                results: [],
                globalData: [],
                externalColumns,
                externalSortColumn,
                externalSortAscending,
                isLoading: true
            },
            () => {
                let queryFilterProps = this.buildAjaxParameters({
                    page: currentPage,
                    filter: currentFilter,
                    orderColumn: currentOrderColumn,
                    orderDir: currentOrderDir,
                    pageSize: currentPageSize
                });

                if (this.props.mutateFilter) {
                    queryFilterProps = this.props.mutateFilter(queryFilterProps);
                }

                /**
                 * Slice off the last "/" if it exists
                 */
                let {url} = this.props;
                if (url.charAt(url.length - 1) === '/') {
                    url = url.slice(0, -1);
                }

                const parameterizedUrl = `${url}?${$.param(queryFilterProps)}`;

                console.debug(`Fetching data from ${url}`, queryFilterProps);

                const handleResponse = response => {
                    const {data} = response;
                    const fetched = data.aaData || data.data || [];
                    const results = extractColumns(externalColumns, fetched);
                    const recordsCount = data.iTotalDisplayRecords || data.recordsFiltered;
                    const maxPages = Math.ceil(recordsCount / currentPageSize);
                    const columnMetadata = this.buildColumnMetadata(this.props.columns, fetched);
                    this.setState({
                        results,
                        columnMetadata,
                        globalData: fetched,
                        maxPages,
                        isLoading: false,
                        draw: this.state.draw + 1
                    });
                    const {onNewDataLoaded} = this.props;
                    if (onNewDataLoaded) {
                        onNewDataLoaded({rawData: data, data: results, total: recordsCount});
                    }
                    return Promise.resolve();
                };

                return axios
                    .get(parameterizedUrl, {cancelToken: createCancelToken()})
                    .then(handleResponse)
                    .catch(error => {
                        if (axios.isCancel(error)) {
                            this.setState({isLoading: false});
                            console.info(
                                'Request canceled',
                                error.message || url,
                                queryFilterProps
                            );
                        } else {
                            this.setState({
                                isLoading: false,
                                error
                            });

                            console.error(
                                'DatatablesGridComponent#getExternalData failure:\n',
                                error
                            );
                        }
                    });
            }
        );
    };

    /**
     * Set the Current Page
     * @param {number} index
     */
    setPage = index => {
        const {maxPages} = this.state;
        const currentPage = clamp(index, 0, maxPages);
        this.setState({currentPage}, this.getExternalData);
    };

    /**
     * Set the Current Page Size
     * @param {number} size
     */
    setPageSize = size => {
        this.setState({currentPage: 0, currentPageSize: size}, this.getExternalData);
    };

    /**
     * Set the Current Sort Column and Sort Ordering
     * @param {string} sortString
     * @param {boolean} sortAscending
     */
    changeSort = (sortString, sortAscending) => {
        const columns = this.props.columns;
        const orderIndex = columns.findIndex(function(item) {
            return item.data === sortString && item.orderable === true;
        });
        if (orderIndex <= -1) {
            // Not an orderable field
            return;
        }
        const orderDir = sortAscending ? SORT_DESCENDING : SORT_ASCENDING;

        this.setState(
            {
                currentOrderColumn: orderIndex,
                currentOrderDir: orderDir
            },
            this.getExternalData
        );
    };

    /**
     * Set the Current Filter
     * @param {string} filter
     */
    setFilter = filter => {
        const {currentFilter, currentPage, savedPage} = this.state;
        if (filter === currentFilter) {
            return;
        }
        const shouldSavePage = !currentFilter && !!filter;
        const shouldRestorePage = !!currentFilter && !filter;
        const nextState = {
            savedPage: shouldSavePage ? currentPage : savedPage,
            currentPage: shouldRestorePage ? savedPage : 0,
            currentFilter: filter
        };
        this.setState(nextState, this.getExternalData);
    };

    /**
     * Change the Current Filter
     * @param {string} filter
     */
    onFilterChange = filter => {
        // @todo This seems really hacky. Shouldn't Griddle be controlling the
        // input state and using a callback? I have a feeling we're using the
        // library incorrectly.
        /** @type {HTMLInputElement} */
        const input = document.querySelector('.griddle-filter input[name=filter]');
        if (input) input.value = filter;
        this.setFilter(filter);
    };

    /**
     * @returns {JSX.Element}
     */
    render() {
        const {
            maxPages,
            currentPage,
            isLoading,
            externalSortColumn,
            externalSortAscending,
            externalColumns,
            currentPageSize,
            results,
            columnMetadata,
            globalData
        } = this.state;

        const {
            filter,
            settings,
            settingsClass,
            rowMetadata,
            metadataColumns,
            customRowComponent,
            customPagerComponent: DatatablePager,
        } = this.props;

        /** @type {GriddleColumnMetadata[]} */
        const columnMeta = columnMetadata.map(column => ({
            ...column,
            refreshCallback: this.getExternalData,
            showNotification: this.props.showNotification,
            changeFilter: this.onFilterChange
        }));

        return (
            <Griddle
                ref={this.props.innerRef}
                columnMetadata={columnMeta}
                columns={externalColumns}
                customRowComponent={customRowComponent}
                // @ts-expect-error
                globalData={globalData}
                enableToggleCustom={false}
                externalChangeSort={this.changeSort}
                externalCurrentPage={currentPage}
                externalIsLoading={isLoading}
                externalLoadingComponent={DatatableLoading}
                externalMaxPage={maxPages}
                externalSetFilter={debounce(this.setFilter, 800)}
                externalSetPage={this.setPage}
                externalSetPageSize={this.setPageSize}
                externalSortAscending={externalSortAscending}
                externalSortColumn={externalSortColumn}
                results={results}
                resultsPerPage={currentPageSize}
                rowMetadata={rowMetadata}
                settingsToggleClassName={settingsClass}
                settingsClass={settingsClass}
                showFilter={filter}
                showSettings={settings}
                sortAscendingComponent={<span className="fa fa-sort-amount-asc pull-left" />}
                sortDescendingComponent={<span className="fa fa-sort-amount-desc pull-left" />}
                useCustomPagerComponent
                customPagerComponent={DatatablePager}
                metadataColumns={metadataColumns}
                useCustomRowComponent={customRowComponent != null}
                useExternal
                useGriddleStyles={false}
            />
        );
    }
}

/** @type {React.ComponentClass<DatatableProps, DatatableState>} */
export const DatatablesGrid = withCancelToken(DatatablesGridComponent);

/**
 * @class ElasticSearchGriddle
 * @template TRowData
 * @extends {Component<DatatableProps, DatatableState>}
 */
class ElasticSearchGriddleComponent extends Component {
    static propTypes = rootPropTypes;
    static displayName = 'ElasticSearch Grid';
    static defaultProps = {
        metadataColumns: ['index']
    };

    /**
     * @param {DatatableProps} props
     */
    constructor(props) {
        super(props);
        /** @type {DatatableState} */
        this.state = {
            ...DatatablesGridComponent.defaultInitialState,
            ...this.props.initialState,
            totalQueryResults: 0,
            currentQuery: {},
            columnMetadata: this.buildColumnMetadata(props.columns),
            globalData: [],
            draw: 0
        };
        this.griddle = React.createRef();
    }

    /**
     * @param {DatatableProps['columns']} rawColumnMetadata
     * @param {Record<string, any>[]} rawResults
     * @returns {GriddleColumnMetadata[]}
     */
    buildColumnMetadata = (rawColumnMetadata, rawResults = []) => {
        /** @type {GriddleColumnMetadata[]} */
        const columnMetadata = rawColumnMetadata.map(rawColumnMetadatum => ({
            columnName: rawColumnMetadatum.data,
            displayName: rawColumnMetadatum.title,
            rawResults,
            ...rawColumnMetadatum,
            ...(rawColumnMetadatum.meta !== undefined ? rawColumnMetadatum.meta : {})
        }));
        return columnMetadata;
    };

    UNSAFE_componentWillMount() {
        if (this.props.loadOnMount) {
            this.getExternalData();
        }
    }

    componentWillUnmount() {
        this.props.cancelRequest();
    }

    /**
     *
     * @param {DatatableProps} prevProps
     */
    componentDidUpdate(prevProps) {
        const {filters: prevFilter} = prevProps;
        const {filters: currFilter} = this.props;

        if (!R.equals(prevFilter, currFilter)) {
            this.getExternalData();
        }
    }

    /**
     * @param {Partial<DatatableState>} stateDiff
     * @param {boolean} [forPaging]
     * @returns {Promise<void>}
     */
    getExternalData = (stateDiff = {}, forPaging = false) => {
        const {
            url,
            filters,
            onNewDataLoaded,
            createCancelToken,
            cancelRequest: cancelPreviousRequest
        } = this.props;

        cancelPreviousRequest();

        const externalColumns = this.props.columns.map(column => column.data);

        const nextState = {
            ...this.state,
            ...stateDiff,
            results: [],
            globalData: [],
            isLoading: true,
            externalColumns,
            currentPage: forPaging ? stateDiff.currentPage : 0
        };

        this.setState(nextState);

        if (!R.isNil(filters)) {
            filters.p = nextState.currentPage;
            filters.pp = nextState.currentPageSize;
        }

        const requestFilters = filters || {
            p: nextState.currentPage,
            pp: nextState.currentPageSize
        };

        console.debug(`Fetching data from ${url}`, requestFilters);

        const handleResponse = response => {
            const {
                data: {results: fetched, total, params}
            } = response;
            const results = extractColumns(externalColumns, fetched);
            const columnMetadata = this.buildColumnMetadata(this.props.columns, fetched);
            if (!R.isNil(results)) {
                const {size} = params;
                this.setState({
                    results,
                    globalData: fetched,
                    columnMetadata,
                    maxPages: total / size,
                    totalQueryResults: total,
                    isLoading: false,
                    error: null,
                    draw: this.state.draw + 1
                });
            } else {
                const {
                    data: {error}
                } = response;

                this.setState({
                    results: [],
                    maxPages: 0,
                    totalQueryResults: 0,
                    isLoading: false,
                    error
                });
            }
            if (onNewDataLoaded) {
                onNewDataLoaded({rawData: response.data});
            }
            return Promise.resolve();
        };

        return axios
            .post(url, requestFilters, {cancelToken: createCancelToken()})
            .then(handleResponse)
            .catch(error => {
                if (axios.isCancel(error)) {
                    this.setState({isLoading: false});
                    console.debug('Request canceled', error.message || url, requestFilters);
                } else {
                    this.setState({
                        isLoading: false,
                        error
                    });

                    console.error(
                        'ElasticSearchGriddleComponent#getExternalData failure:\n',
                        error
                    );
                }
            });
    };

    /**
     * Set the Current Page
     * @param {number} index
     */
    setPage = index => {
        const {maxPages} = this.state;
        const currentPage = clamp(index, 0, maxPages);
        this.getExternalData({currentPage}, true);
    };

    /**
     * Set the Current Page Size
     * @param {number} size
     */
    setPageSize = size => {
        this.getExternalData({
            currentPage: 0, // when we change the page size go back to the first page
            currentPageSize: size
        });
    };

    /**
     * Set the Current Sort Column and Sort Ordering
     * @param {string} sort
     * @param {boolean} sortAscending
     */
    changeSort(sort, sortAscending) {
        const columns = this.props.columns;
        const orderIndex = columns.findIndex(function(item) {
            return item.data === sort && item.orderable === true;
        });
        if (orderIndex <= -1) {
            // Not an orderable field
            return;
        }
        const orderDir = sortAscending ? SORT_DESCENDING : SORT_ASCENDING;
        this.getExternalData({
            currentOrderColumn: orderIndex,
            currentOrderDir: orderDir
        });
    }

    /**
     * Set the Current Filter
     * @param {string} filter
     */
    setFilter = filter => {
        const {currentFilter, currentPage, savedPage} = this.state;
        if (filter === currentFilter) {
            return;
        }
        const shouldSavePage = !currentFilter && !!filter;
        const shouldRestorePage = !!currentFilter && !filter;
        const newState = {
            savedPage: shouldSavePage ? currentPage : savedPage,
            currentPage: shouldRestorePage ? savedPage : 0,
            currentFilter: filter
        };

        this.getExternalData(newState);
    };

    /**
     * @returns {JSX.Element}
     */
    render() {
        const {
            results,
            maxPages,
            currentPage,
            isLoading,
            externalSortColumn,
            externalSortAscending,
            externalColumns,
            currentPageSize,
            totalQueryResults,
            columnMetadata,
            error,
            globalData
        } = this.state;
        const {
            filter,
            show,
            settingsClass,
            rowMetadata,
            metadataColumns,
            customRowComponent
        } = this.props;

        if (!R.isNil(error)) {
            return (
                <div className="row">
                    <div className="col-md-4">{JSON.stringify(error)}</div>
                </div>
            );
        }

        return (
            <div>
                {isLoading && (
                    <div className="text-center">
                        <LoadingSpinner additionalClasses="fa-2x" />
                    </div>
                )}
                {!isLoading && (
                    <div className="results-metadata-container">
                        <div className="col-md-4">
                            <div>{`${totalQueryResults} search results`}</div>
                        </div>
                    </div>
                )}
                <Griddle
                    ref={this.props.innerRef}
                    useExternal
                    externalSetPage={this.setPage}
                    externalChangeSort={this.changeSort}
                    externalSetFilter={this.setFilter}
                    externalSetPageSize={this.setPageSize}
                    externalMaxPage={maxPages}
                    externalCurrentPage={currentPage}
                    externalIsLoading={isLoading}
                    externalSortColumn={externalSortColumn}
                    externalSortAscending={externalSortAscending}
                    externalLoadingComponent={DatatableLoading}
                    // @ts-expect-error
                    globalData={globalData}
                    metadataColumns={metadataColumns}
                    showFilter={filter}
                    results={results}
                    resultsPerPage={currentPageSize}
                    columns={externalColumns}
                    columnMetadata={columnMetadata}
                    sortAscendingComponent={<span className="fa fa-sort-amount-asc pull-left" />}
                    sortDescendingComponent={<span className="fa fa-sort-amount-desc pull-left" />}
                    useCustomPagerComponent
                    customPagerComponent={DatatablePager}
                    showSettings={show}
                    settingsToggleClassName={settingsClass}
                    settingsClass={settingsClass}
                    rowMetadata={rowMetadata}
                    useCustomRowComponent={customRowComponent !== undefined}
                    customRowComponent={customRowComponent}
                    enableToggleCustom={false}
                    useGriddleStyles={false}
                />
            </div>
        );
    }
}

export const ElasticSearchGriddle = withCancelToken(ElasticSearchGriddleComponent);

/**
 * @class CustomSQLGriddle
 * @template TRowData
 * @extends {Component<DatatableProps, DatatableState>}
 */
class CustomSQLGriddleComponent extends Component {
    static propTypes = rootPropTypes;
    static displayName = 'CustomSQLGriddle';
    static defaultProps = {
        metadataColumns: ['index']
    };

    /**
     * @param {DatatableProps} props
     */
    constructor(props) {
        super(props);
        const {columns} = props;
        const externalColumns = columns.map(c => c.field_alias);
        this.state = {
            ...DatatablesGridComponent.defaultInitialState,
            ...this.props.initialState,
            columnMetadata: this.buildColumnMetadata(props.columns),
            externalColumns,
            globalData: []
        };
        this.latestRequestId = 0;
        this.griddle = React.createRef();
        // build searchable fields
        /** @type {string[]} */
        this.searchableFields = props.columns
            .map(column => {
                const isSearchable = column.meta && column.meta.searchable;
                if (isSearchable) {
                    return column.field_name;
                }
                return null;
            })
            .filter(column => column !== null);
    }

    componentDidMount() {
        if (this.props.loadOnMount) {
            this.getExternalData();
        }
    }

    /**
     * @param {DatatableProps} prevProps
     */
    componentDidUpdate(prevProps) {
        const {
            url: prevUrl,
            filter: prevFilter,
            hidden: prevHidden,
            columns: prevColumns,
            initialState: {
                currentOrderDir: prevOrderDir
            },
            customConfiguration: prevCustomConfiguration
        } = prevProps;
        const {
            url: currUrl,
            filter: currFilter,
            hidden: currHidden,
            columns: currColumns,
            initialState: {
                currentOrderDir: currOrderDir
            },
            customConfiguration: currCustomConfiguration
        } = this.props;
        if (
            (prevHidden && !currHidden) ||
            currUrl !== prevUrl ||
            !R.equals(prevFilter, currFilter) ||
            !R.equals(prevColumns, currColumns) ||
            !R.equals(prevOrderDir, currOrderDir) ||
            !R.equals(prevCustomConfiguration, currCustomConfiguration)
        ) {
            const externalColumns = currColumns.map(c => c.field_alias);
            this.setState({
                externalColumns,
                currentOrderDir: currOrderDir
            }, this.getExternalData);
        }
    }
    /**
     * @param {DatatableProps['columns']} rawColumnMetadata
     * @param {Record<string, any>[]} rawResults
     * @returns {GriddleColumnMetadata[]}
     */
    buildColumnMetadata = (rawColumnMetadata, rawResults = []) => {
        /** @type {GriddleColumnMetadata[]} */
        const columnMetadata = rawColumnMetadata.map(rawColumnMetadatum => ({
            columnName: rawColumnMetadatum.field_alias,
            rawResults,
            ...rawColumnMetadatum,
            ...(rawColumnMetadatum.meta !== undefined ? rawColumnMetadatum.meta : {})
        }));
        return columnMetadata;
    };

    /**
     * Set the Current Page
     * @param {number} index
     */
    setPage = index => {
        const {maxPages} = this.state;
        const currentPage = clamp(index, 0, maxPages);
        this.getExternalData({currentPage});
    };

    /**
     * Set the Current Page Size
     * @param {number} size
     */
    setPageSize = size => {
        this.getExternalData({
            currentPage: 0, // when we change the page size go back to the first page
            currentPageSize: size
        });
    };

    /**
     * Set the Current Filter
     * @param {string} filter
     */
    setFilter = filter => {
        const {currentFilter, currentPage, savedPage} = this.state;
        if (filter === currentFilter) {
            return;
        }
        const shouldSavePage = !currentFilter && !!filter;
        const shouldRestorePage = !!currentFilter && !filter;
        const newState = {
            savedPage: shouldSavePage ? currentPage : savedPage,
            currentPage: shouldRestorePage ? savedPage : 0,
            currentFilter: filter
        };

        this.getExternalData(newState);
    };

    /**
     * Change the Current Filter
     * @param {string} filter
     */
    changeFilter = filter => {
        /** @type HTMLInputElement */
        const input = document.querySelector('.griddle-filter input[name=filter]');
        if (input) input.value = filter;
        return this.setFilter(filter);
    };

    /**
     * Set the Current Sort Column and Sort Ordering
     * @param sort
     * @param sortAscending
     */
    changeSort(sort, sortAscending) {
        const {columns} = this.props;
        const orderIndex = columns.findIndex(function(item) {
            return item.field_alias === sort && item.meta.orderable === true;
        });
        if (orderIndex <= -1) {
            // Not an orderable field
            return;
        }
        const orderDir = sortAscending ? SORT_DESCENDING : SORT_ASCENDING;
        this.getExternalData({
            currentOrderColumn: orderIndex,
            currentOrderDir: orderDir
        });
    }

    /**
     * @param {Partial<DatatableState>} [stateDiff]
     */
    getExternalData = stateDiff => {
        const {
            columns,
            customConfiguration,
            createCancelToken,
            cancelRequest,
            orderColumnName: defaultOrderColumnName
        } = this.props;

        const {externalColumns} = this.state;
        let updatedState = stateDiff ? R.merge(this.state, stateDiff) : this.state;

        const orderColumn = updatedState.currentOrderColumn;
        const orderDir = updatedState.currentOrderDir;

        const orderColumnDefined = orderColumn !== undefined;

        const orderColumnName = orderColumnDefined ? columns[orderColumn].field_alias : '';
        const orderColumnMeta = orderColumnDefined ? columns[orderColumn].meta : {};
        const orderSortColumn = orderDir != null ? orderDir === SORT_DESCENDING : true;
        const orderOp = R.prop('orderOp', orderColumnMeta);

        // Cancel any in-flight requests
        cancelRequest();

        updatedState = R.merge(updatedState, {
            results: [],
            globalData: [],
            externalSortColumn: orderColumnName,
            externalSortAscending: orderSortColumn,
            isLoading: true
        });

        if (customConfiguration) {
            updatedState = R.merge(updatedState, {customConfiguration});
        }
        this.setState(updatedState, () => {
            // build stupid filter
            let filter = {
                pagination: {
                    p: updatedState.currentPage,
                    pp: updatedState.currentPageSize
                },
                search: {
                    searchable_fields: this.searchableFields,
                    search_value: updatedState.currentFilter
                },
                order: {
                    order_field: orderColumnName,
                    direction: orderDir,
                    order_op: orderOp || ''
                }
            };

            if (customConfiguration) {
                filter = R.merge(filter, {customConfiguration});
            }

            /**
             * Slice off the last "/" if it exists
             */
            let {url} = this.props;
            if (url.charAt(url.length - 1) === '/') {
                url = url.slice(0, -1);
            }

            const getEditRequests = response => {
                if(window.Cypress) return response;
                const {
                    data: {results: fetched}
                } = response;
                const ids = fetched?.map(f => f.content_metadata_id).filter(id => !!id);
                return queryEditRequest(ids, true).then((res) => {
                    const contents = fetched?.map(f => {
                        const editRequests = res?.editRequests?.filter(eR => Number(eR.contentId) === f.content_metadata_id);
                        f.editRequests = editRequests.map(eR => {
                            return {
                                user_email: eR.emailAddress,
                                activity_type: 'ops_request_fix',
                                activity_message: eR.message,
                                activity_timestamp: eR.createdAt
                            }
                        })
                        return f;
                    }) || [];
                    if (response?.data)
                        response.data.results = contents;
                    return response;
                });
            }

            const handleResponse = response => {
                const {
                    data: {results: fetched, total, error}
                } = response;
                const results = extractColumns(externalColumns, fetched);
                const columnMetadata = this.buildColumnMetadata(columns, fetched);

                if (!R.isNil(results)) {
                    this.setState({
                        results,
                        globalData: fetched,
                        columnMetadata,
                        maxPages: total / updatedState.currentPageSize,
                        totalQueryResults: total,
                        isLoading: false,
                        error: null
                    });
                } else {
                    this.setState({
                        results: [],
                        maxPages: 0,
                        totalQueryResults: 0
                    });
                    throw error;
                }
                return Promise.resolve();
            };

            console.debug(`Fetching data from ${url}`, filter);

            /**
             * Should this really be using POST? I think it should be using `params`
             * in the options instead. - Parker
             */
            axios
                .post(url, filter, {cancelToken: createCancelToken()})
                .then(getEditRequests)
                .then(handleResponse)
                .catch(error => {
                    if (axios.isCancel(error)) {
                        this.setState({isLoading: false});
                        console.debug('Request canceled', error.message || url, filter);
                    } else {
                        this.setState({
                            isLoading: false,
                            error
                        });
                        console.error('CustomSQLGriddle#getExternalData failure:\n', error);
                    }
                });
        });
    };

    render() {
        const {
            results,
            columnMetadata,
            maxPages,
            currentPage,
            isLoading,
            externalSortColumn,
            externalSortAscending,
            externalColumns,
            currentPageSize,
            globalData,
            error
        } = this.state;
        const {
            filter,
            show,
            settingsClass,
            rowMetadata,
            metadataColumns,
            customRowComponent,
            classes
        } = this.props;

        // Inject the RefreshCallback into the ColumnMetadata
        /** @type *[] */
        const columnMeta = columnMetadata.map(metadatum => ({
            ...metadatum,
            refreshCallback: this.getExternalData,
            showNotification: this.props.showNotification,
            changeFilter: this.changeFilter
        }));

        if (error != null) {
            const errorMessage = error.message && error.message.length ? `: ${error.message}` : '';

            return (
                <div className="row">
                    <div className="col-md-8">
                        Error{errorMessage}. Try reloading the page, or contact support for
                        assistance.
                    </div>
                </div>
            );
        }

        const griddleContainerClass = classNames(classes);

        return (
            <div className={griddleContainerClass}>
                <Griddle
                    ref={this.props.innerRef}
                    useExternal
                    externalSetPage={this.setPage}
                    externalChangeSort={this.changeSort}
                    externalSetFilter={debounce(this.setFilter, 1000)}
                    externalSetPageSize={this.setPageSize}
                    externalMaxPage={maxPages}
                    externalCurrentPage={currentPage}
                    externalIsLoading={isLoading}
                    externalSortColumn={externalSortColumn}
                    externalSortAscending={externalSortAscending}
                    externalLoadingComponent={DatatableLoading}
                    // @ts-expect-error
                    globalData={globalData}
                    showFilter={filter}
                    metadataColumns={metadataColumns}
                    results={results}
                    resultsPerPage={currentPageSize}
                    columns={externalColumns}
                    columnMetadata={columnMeta}
                    sortAscendingComponent={<span className="fa fa-sort-amount-asc pull-left" />}
                    sortDescendingComponent={<span className="fa fa-sort-amount-desc pull-left" />}
                    useCustomPagerComponent
                    customPagerComponent={DatatablePager}
                    showSettings={show}
                    settingsToggleClassName={settingsClass}
                    settingsClass={settingsClass}
                    rowMetadata={rowMetadata}
                    useCustomRowComponent={customRowComponent !== undefined}
                    customRowComponent={customRowComponent}
                    enableToggleCustom={false}
                    useGriddleStyles={false}
                />
            </div>
        );
    }
}

export const CustomSQLGriddle = withCancelToken(CustomSQLGriddleComponent);



// WEBPACK FOOTER //
// ./components/griddle_datatable.js